import { useFieldArray, useFormContext } from 'react-hook-form';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import { PlatformAppUiSchema } from '@typings';

import { Button, Field, Icon, Render, Table, Theme } from '@components';

const NUMBER_TYPES = ['number', 'integer', 'float'];

type ArrayItem = {
  id: string;
};

type Props = {
  name: PlatformAppUiSchema.FieldProperties['name'];
  required?: PlatformAppUiSchema.FieldProperties['required'];
  label?: PlatformAppUiSchema.FieldProperties['title'];
  error?: string;
  note?: PlatformAppUiSchema.FieldProperties['description'];
  items?: PlatformAppUiSchema.FieldProperties['items'];
};

export const PlatformAppArray: React.FC<Props> = ({
  name,
  required,
  error,
  label,
  note,
  items,
}) => {
  const { register, control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const isNumber = !!items?.type && NUMBER_TYPES.includes(items.type);

  const makeItem = ({ id }: ArrayItem, index: number) => {
    const type = isNumber ? 'number' : 'text';

    return (
      <Table.Row key={id}>
        <Table.Cell className="border-0 p-0">
          <Field.Input
            {...register(`${name}.${index}`, { valueAsNumber: isNumber })}
            className="rounded-r-none bg-neural-01"
            type={type}
          />
        </Table.Cell>
        <Table.Cell className="rounded-r-lg border-0 bg-neural-01 py-0">
          <Button theme onClick={() => remove(index)}>
            <Icon icon={faTrashAlt} className="text-[18px] text-error" />
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-5 rounded-lg">
        <Render if={label}>
          <Field.Label required={required} className="mb-0 font-500 capitalize">
            {label}
          </Field.Label>
        </Render>
        <Render if={fields}>
          <Theme.Container className="p-0 shadow-none">
            <Table className="w-full grid-cols-[auto_min-content] gap-y-2 overflow-auto rounded-none">
              <Table.Body>{fields.map(makeItem)}</Table.Body>
            </Table>
          </Theme.Container>
        </Render>
        <div className="flex justify-end">
          <Button className="capitalize" onClick={() => append('')}>
            <Icon icon={faPlus} />
            Add entry
          </Button>
        </div>
      </div>
      <Field.Error className="-mt-4">{error}</Field.Error>
      <Render if={!error}>
        <Field.Note className="-mt-4">{note}</Field.Note>
      </Render>
    </>
  );
};
